import { useState } from "react";
import { createPortal } from "react-dom";
import { Share2, Link2, X as Close } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const P = {
  whatsapp: "M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.3-.5 0-1 .2-3.3-.7-2.8-1.1-4.5-4-4.7-4.2-.1-.2-1.1-1.5-1.1-2.9s.7-2 1-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.9 2.1c.1.2.1.4 0 .5l-.4.6-.4.4c-.1.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.1 1 2.1 1.3 2.4 1.5.3.1.5.1.7-.1l.9-1.1c.2-.3.4-.2.7-.1l2 1c.3.1.5.2.5.3.1.2.1.7-.1 1.3Z",
  facebook: "M13.5 22v-8.2h2.8l.4-3.2h-3.2V8.5c0-.9.3-1.6 1.6-1.6h1.7V4.1a23 23 0 0 0-2.5-.1c-2.5 0-4.2 1.5-4.2 4.3v2.4H7.3v3.2h2.8V22h3.4Z",
  x: "M17.8 3h3.1l-6.8 7.7L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.2-8.3L2 3h6.4l4.4 5.8L17.8 3Zm-1.1 16.2h1.7L7.4 4.7H5.6l11.1 14.5Z",
  telegram: "M21.9 4.3 18.8 19c-.2 1-.8 1.3-1.7.8l-4.6-3.4-2.2 2.1c-.3.3-.5.5-1 .5l.3-4.7 8.6-7.8c.4-.3-.1-.5-.6-.2L7 13l-4.6-1.4c-1-.3-1-1 .2-1.5l18-6.9c.8-.3 1.6.2 1.3 1.1Z",
};
const Brand = ({ d }) => <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d={d} /></svg>;

const OPTIONS = [
  { id: "whatsapp", label: "WhatsApp", color: "#25D366", url: (u, t) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}` },
  { id: "facebook", label: "Facebook", color: "#1877F2", url: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { id: "x", label: "X", color: "currentColor", url: (u, t) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}&url=${encodeURIComponent(u)}` },
  { id: "telegram", label: "Telegram", color: "#229ED9", url: (u, t) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
];

const copy = async (u) => {
  try { await navigator.clipboard.writeText(u); } catch {
    const el = document.createElement("textarea"); el.value = u; document.body.appendChild(el); el.select(); document.execCommand("copy"); el.remove();
  }
};

function Panel({ kind, id, url, text, onClose }) {
  const pick = (ch) => {
    api.post("/asv/share", { kind, key: id, channel: ch }).catch(() => {});
    onClose();
  };
  const tid = `share-${kind}-${id}`.replace(/[^a-zA-Z0-9-]/g, "-");
  const close = (e) => { e.preventDefault(); e.stopPropagation(); onClose(); };
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 sm:items-center" onClick={close} data-testid={`${tid}-panel`}>
      <div className="asv-card w-full rounded-b-none p-5 sm:w-[380px] sm:rounded-b-[1.5rem]" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><p className="asv-display text-lg font-extrabold">Share</p>
          <button type="button" onClick={close} className="asv-muted grid h-8 w-8 place-items-center rounded-full" aria-label="Close" data-testid={`${tid}-close`}><Close className="h-4 w-4" /></button></div>
        <div className="grid grid-cols-5 gap-2">
          {OPTIONS.map((o) => (
            <a key={o.id} href={o.url(url, text)} target="_blank" rel="noopener noreferrer" onClick={() => pick(o.id)} data-testid={`${tid}-${o.id}`}
              className="flex flex-col items-center gap-1.5 rounded-2xl p-2 text-[11px] font-semibold transition-transform hover:-translate-y-0.5">
              <span className="asv-soft grid h-11 w-11 place-items-center rounded-full" style={{ color: o.color }}><Brand d={P[o.id]} /></span>{o.label}</a>))}
          <button type="button" onClick={async () => { await copy(url); toast.success("Link copied"); pick("copy"); }} data-testid={`${tid}-copy`}
            className="flex flex-col items-center gap-1.5 rounded-2xl p-2 text-[11px] font-semibold transition-transform hover:-translate-y-0.5">
            <span className="asv-soft grid h-11 w-11 place-items-center rounded-full"><Link2 className="h-5 w-5" /></span>Copy link</button>
        </div>
        <p className="asv-muted mt-4 truncate rounded-xl border asv-line px-3 py-2 text-xs" data-testid={`${tid}-url`}>{url}</p>
      </div>
    </div>, document.querySelector("[data-testid='asv-shell']") || document.body);
}

export function ShareButton({ kind, id, path, text, icon = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const url = `${window.location.origin}${path}`;
  const tid = `share-${kind}-${id}`.replace(/[^a-zA-Z0-9-]/g, "-");
  const click = (e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); };
  return (
    <>
      {icon
        ? <button type="button" onClick={click} aria-label="Share" data-testid={`${tid}-button`} className={`grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white backdrop-blur transition-transform hover:scale-110 ${className}`}><Share2 className="h-4 w-4" /></button>
        : <button type="button" onClick={click} data-testid={`${tid}-button`} className={`asv-soft inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors hover:opacity-90 ${className}`}><Share2 className="h-4 w-4 asv-accent" />Share</button>}
      {open && <Panel kind={kind} id={id} url={url} text={text} onClose={() => setOpen(false)} />}
    </>
  );
}

export const shareText = {
  venue: (x) => `${x.name} at ${x.vendor_name} – book on All Sports Venues`,
  event: (x, date) => `${x.title}, ${date} – get tickets on All Sports Venues`,
  game: (x, date) => `Join my ${x.sport} game on ${date} at ${x.venue_name} – All Sports Venues`,
  centre: (x) => `${x.name} – book courts and pitches on All Sports Venues`,
};
